import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * The panel's surfaces.
 *
 * One border radius, one border colour, one padding scale. Every screen here
 * used to hand-roll `rounded-lg border border-border p-3`, which is fine until
 * two of them disagree by two pixels and the page looks assembled rather than
 * designed.
 */

export function Card({
  className,
  children,
  ...rest
}: { className?: string; children: ReactNode } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('rounded-xl border border-border bg-card text-card-foreground', className)}
      {...rest}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  hint,
  action,
  className,
}: {
  title: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex items-start justify-between gap-3 px-4 pb-3 pt-4', className)}>
      <div className="min-w-0">
        <h2 className="truncate text-sm font-semibold tracking-tight">{title}</h2>
        {hint ? <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

/**
 * One number, its label, and — where it exists — the second fact that stops the
 * number being misread.
 *
 * "3 stationary" invites the question "out of how many, and since when";
 * `sub` is where that answer goes, next to the figure rather than a scroll away.
 */
export function StatTile({
  label,
  value,
  sub,
  tone = 'default',
  icon,
  className,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: 'default' | 'moving' | 'stopped' | 'warning' | 'danger' | 'success';
  icon?: ReactNode;
  className?: string;
}) {
  const accent = {
    default: 'text-foreground',
    moving: 'text-track-moving',
    stopped: 'text-track-stopped',
    warning: 'text-warning',
    danger: 'text-danger',
    success: 'text-success',
  }[tone];

  return (
    <Card className={cn('p-4', className)}>
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        {icon}
        <span className="truncate">{label}</span>
      </div>
      {/* tabular-nums so a polling number does not shuffle the row width as it
          ticks — a dashboard that jitters reads as unstable. */}
      <div className={cn('mt-1.5 text-2xl font-semibold tabular-nums tracking-tight', accent)}>
        {value}
      </div>
      {sub ? <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div> : null}
    </Card>
  );
}

/** A label/value pair for the dense metric grids inside a detail panel. */
export function Metric({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: 'moving' | 'stopped' | 'idle' | 'gap';
}) {
  const accent = tone
    ? {
        moving: 'text-track-moving',
        stopped: 'text-track-stopped',
        idle: 'text-track-idle',
        gap: 'text-track-gap',
      }[tone]
    : 'text-foreground';

  return (
    <div className="rounded-lg bg-muted/50 px-3 py-2">
      <div className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className={cn('mt-0.5 text-sm font-semibold tabular-nums', accent)}>{value}</div>
      {sub ? <div className="text-2xs text-muted-foreground">{sub}</div> : null}
    </div>
  );
}
